import React, { useEffect, useState } from 'react';
import { useIsOnline } from '../hooks/useIsOnline';
import { getSyncState, subscribeSync } from '../hooks/useFirestoreSyncMonitor';
import { isClientDead, subscribeClientHealth } from '../lib/clientHealth';

/**
 * SaveIndicator — header-mounted indicator that visualises the save /
 * sync state of the user's edits. Replaces the older spinner that just
 * said "saving..." regardless of whether the user was actively typing,
 * waiting on a debounce, or actually mid-write.
 *
 * States (driven by the `status` prop, which BoardPage assembles from
 * onSaveStatus calls from ListPanel / FocusedPanel etc.):
 *
 *   - 'synced'  → ✓ + "synced"  (both local IndexedDB + cloud up to date)
 *   - 'typing'  → 💾 + "typing"  (keystroke fired in the last ~500ms)
 *   - 'pending' → 💾 + countdown ("3s", "2s", "1s") between the typing-
 *                 idle moment and the moment the debounce timer fires
 *   - 'syncing' → 💾 (pulsing) + "syncing"  (actively writing to Firestore)
 *
 * The 'local' caption is derived: when status === 'synced' AND the
 * browser is offline (navigator.onLine === false via useIsOnline),
 * the indicator renders ✓ + "local" instead. Same icon (the data IS
 * durable on the device — Firestore's IndexedDB persistence keeps it
 * safe); just a caption shift so the user knows the cloud half of the
 * sync hasn't completed.
 *
 * Tap-to-force-sync: when status is 'typing' or 'pending', tapping the
 * floppy 💾 calls onForceSync — bypasses the 4-second debounce and
 * commits immediately. No-op in other states (already in motion, or
 * nothing to flush).
 *
 * Walter 2026-05-15: built to replace the opaque "saving / saved /
 * idle" spinner. See DESIGN_LOG ("Save indicator + sync vs save
 * vocabulary") for the full rationale.
 */

export default function SaveIndicator({ status, onForceSync, compact = false, queuedCount = 0 }) {
  const isOnline = useIsOnline();
  // Walter 2026-05-16: subscribe to the Firestore sync monitor so we
  // can show a distinct "reconnecting" state while the listener is
  // serving from local cache (i.e. between foregrounding the app and
  // the listener catching up with the server). Pure read-only — no
  // additional Firestore reads.
  const [syncStale, setSyncStale] = useState(() => getSyncState().isStale);
  useEffect(() => {
    return subscribeSync(() => setSyncStale(getSyncState().isStale));
  }, []);
  // Walter 2026-09-06: the check-mark was the thing that lied. His PWA window
  // showed "synced" for 22 hours over day-old content, because the caption is
  // assembled from THIS device's write queue plus its own idea of
  // connectivity — neither of which notices that the window has stopped
  // hearing from the server. When lib/clientHealth latches this window dead
  // the indicator says so, and clicking it reloads, because reloading is the
  // only thing that fixes it. Ranked above every other resting state: a
  // window that isn't receiving has nothing truthful to say about being
  // in sync.
  const [clientDead, setClientDead] = useState(() => isClientDead());
  useEffect(() => subscribeClientHealth(setClientDead), []);
  // Used to tick the countdown render every 500ms while in 'pending'
  // state. Storing a counter rather than the timestamp directly so we
  // re-render on a predictable cadence; the real "save fires at N
  // seconds" math lives in the renderer below.
  const [, forceRerender] = useState(0);
  // Timestamp of the most recent transition INTO 'pending'. Counter
  // displays seconds-remaining based on this anchor.
  const [pendingStartedAt, setPendingStartedAt] = useState(null);
  // Walter 2026-05-15: the check-mark briefly highlights in accent
  // (orange) when sync just completed so the success is visible,
  // then settles to dim grey after ~1.5s. The accent state reads as
  // "something just happened — good"; the dim state reads as "in
  // sync, nothing to worry about." Without the fade the orange ✓ is
  // too loud at rest.
  const [syncedJust, setSyncedJust] = useState(false);
  useEffect(() => {
    if (status !== 'synced') {
      setSyncedJust(false);
      return undefined;
    }
    setSyncedJust(true);
    const t = setTimeout(() => setSyncedJust(false), 1500);
    return () => clearTimeout(t);
  }, [status]);

  useEffect(() => {
    if (status === 'pending') {
      setPendingStartedAt((prev) => prev ?? Date.now());
    } else {
      setPendingStartedAt(null);
    }
  }, [status]);

  useEffect(() => {
    if (status !== 'pending') return undefined;
    const id = setInterval(() => forceRerender((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [status]);

  // Derive what to render.
  // The "displayed status" can differ from the prop when offline +
  // synced — we show 'local' as a caption variation of the synced
  // icon. Walter 2026-05-16: ALSO when the Firestore listener is
  // serving from local cache (syncStale=true && online), we override
  // the resting state to a "reconnecting" indication so the user
  // knows their edits aren't reaching other devices yet. This is
  // distinct from `local` (offline) — here the device IS online but
  // the listener hasn't caught up.
  const isOfflineSynced = status === 'synced' && !isOnline;
  const isReconnecting = status === 'synced' && isOnline && syncStale;
  // Walter 2026-08-12: `unsynced` — an edit is saved on this device but the
  // cloud hasn't accepted it. DISTINCT from `local`, which means the browser
  // knows it's offline: this one fires while ONLINE, which is the case that
  // produced three of his conflicts (a write failed, was queued, and the app
  // showed a synced checkmark anyway). Derived from the real queue, so it
  // clears itself the moment the retry lands. Ranked below the in-motion
  // states so the countdown he likes is untouched.
  const hasUnsynced = queuedCount > 0 && status !== 'typing' && status !== 'pending' && status !== 'syncing';
  const caption =
    clientDead           ? 'reload' :
    status === 'typing'  ? 'typing' :
    status === 'pending' ? formatCountdown(pendingStartedAt) :
    status === 'syncing' ? 'syncing' :
    hasUnsynced          ? 'unsynced' :
    isReconnecting       ? 'reconnecting' :
    isOfflineSynced      ? 'local' :
    /* synced */           'synced';
  const isCheckState = status === 'synced' && !isReconnecting && !hasUnsynced && !clientDead;
  const isClickable = clientDead || status === 'typing' || status === 'pending';
  const isPulsing = !clientDead && (status === 'syncing' || isReconnecting);

  const handleClick = () => {
    if (clientDead) { window.location.reload(); return; }
    if (!isClickable) return;
    onForceSync?.();
  };

  // Sizing — slightly smaller in compact (mobile header) mode.
  // Walter 2026-05-15: trimmed down a notch (was 16/18) so the floppy
  // + check don't feel heavier than the bell / cog / search neighbours
  // in the header chrome.
  const iconPx = compact ? 14 : 15;
  const captionFontPx = compact ? 9 : 9.5;
  const slotWidth = compact ? 44 : 56;

  return (
    <div
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      title={
        clientDead ? 'this window may be out of date — click to reload' :
        isClickable ? 'tap to sync now' :
        hasUnsynced ? `${queuedCount} edit${queuedCount === 1 ? '' : 's'} saved on this device but not yet in the cloud — retrying every minute` :
        isOfflineSynced ? 'saved on this device; offline so not yet synced to the cloud' :
        isReconnecting ? 'reconnecting to sync — edits may not appear on other devices for a few seconds' :
        isPulsing ? 'syncing to cloud…' :
        'in sync'
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1px',
        width: `${slotWidth}px`,
        minHeight: `${iconPx + captionFontPx + 6}px`,
        flexShrink: 0,
        cursor: isClickable ? 'pointer' : 'default',
        userSelect: 'none',
        // Subtle hover bg on the clickable states; nothing on the
        // resting states.
        transition: 'background 0.15s',
        borderRadius: 'var(--radius-sm, 4px)',
        padding: '2px',
      }}
      onMouseEnter={(e) => {
        if (isClickable) e.currentTarget.style.background = 'var(--surface2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{
        width: `${iconPx}px`,
        height: `${iconPx}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Check is accent-orange for ~1.5s after sync completes
        // ("just happened — good"), then settles to dim grey. Floppy
        // (typing / pending / syncing) is always dim — those states
        // are "in progress" and don't need to shout.
        color: clientDead
          ? 'var(--accent)'
          : isCheckState
            ? (syncedJust ? 'var(--accent)' : 'var(--text-dim)')
            : 'var(--text-dim)',
        animation: isPulsing ? 'lists-save-pulse 1s ease-in-out infinite' : undefined,
        transition: 'color 0.4s ease',
      }}>
        {isCheckState ? <CheckIcon size={iconPx} /> : <SyncIcon size={iconPx} />}
      </div>
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: `${captionFontPx}px`,
        lineHeight: 1,
        color: clientDead
          ? 'var(--accent)'
          : isCheckState
            ? (syncedJust ? 'var(--accent)' : 'var(--text-dim)')
            : 'var(--text-dim)',
        letterSpacing: '0.3px',
        // Reserve a fixed-width slot so the layout doesn't jitter
        // as the caption text changes length between states.
        textAlign: 'center',
        whiteSpace: 'nowrap',
        transition: 'color 0.4s ease',
      }}>
        {caption}
      </div>
    </div>
  );
}

function formatCountdown(pendingStartedAt) {
  if (!pendingStartedAt) return '…';
  // Pending started ~500ms after the last keystroke. The actual save
  // fires at 4000ms after the last keystroke. So at the moment we
  // enter 'pending', ~3.5s remain. Show that as "3s" (rounded up to
  // the next whole second so the user sees a tick rather than starting
  // with a fractional number).
  const elapsedMs = Date.now() - pendingStartedAt;
  // Time-remaining in the debounce: 3500 - elapsed (because pending
  // started 500ms post-keystroke, save fires at 4000ms post-keystroke).
  const remainingMs = Math.max(0, 3500 - elapsedMs);
  const remainingS = Math.max(1, Math.ceil(remainingMs / 1000));
  return `${remainingS}s`;
}

function SyncIcon({ size = 16 }) {
  // Walter 2026-05-16: replaced the floppy disk silhouette (which kept
  // reading as visually heavier than the neighbouring icons even after
  // two simplification passes) with the conventional "sync" glyph —
  // two horizontal arrows pointing in opposite directions. Reads
  // immediately as "data going both ways" / "in motion" without
  // carrying the dated floppy connotation, and matches the line-art
  // weight of the bell / cog / search icons.
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" aria-hidden="true"
      style={{ display: 'block' }}
    >
      {/* Top arrow: right-pointing */}
      <path d="M3 5 L12 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M10 3 L12 5 L10 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      {/* Bottom arrow: left-pointing */}
      <path d="M4 11 L13 11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M6 9 L4 11 L6 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}

function CheckIcon({ size = 16 }) {
  // Walter 2026-05-15: stroke thinned (was 1.8 → 1.4) so the check
  // sits at the same visual weight as the bell / cog / search icons
  // next to it in the header. The 1.8 stroke read as deliberately
  // chunky against its slimmer neighbours.
  return (
    <svg
      width={size} height={size} viewBox="0 0 16 16"
      fill="none" aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path
        d="M3 8.5l3.2 3.2L13 5"
        stroke="currentColor" strokeWidth="1.4"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}
